import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { VerificationStatus } from "@/api.types";
import { useSession } from "@/services/auth";

const API_URL = import.meta.env.VITE_API_URL;

interface SendCodeResponse {
  success: boolean;
}

interface SendCodeError {
  error: string;
  cooldownRemaining?: number;
}

interface VerifyCodeResponse {
  success: boolean;
  verified: boolean;
}

interface VerifyCodeError {
  error: string;
  attemptsRemaining?: number;
}

/**
 * Get current verification status
 */
export const useVerificationStatus = () => {
  const { token, isLoggedIn } = useSession();

  return useQuery({
    queryKey: ["verification", "status"],
    queryFn: async () => {
      const response = await axios.get<VerificationStatus>(
        `${API_URL}/api/v2/verification/status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: isLoggedIn && !!token,
    staleTime: 1000 * 60, // Consider fresh for 1 minute
  });
};

/**
 * Send email verification code
 */
export const useSendEmailCode = () => {
  const { token } = useSession();

  return useMutation<SendCodeResponse, SendCodeError, void>({
    mutationFn: async () => {
      const response = await axios.post<SendCodeResponse>(
        `${API_URL}/api/v2/verification/email/send`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
  });
};

/**
 * Verify email code
 */
export const useVerifyEmailCode = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<VerifyCodeResponse, VerifyCodeError, { code: string }>({
    mutationFn: async ({ code }) => {
      const response = await axios.post<VerifyCodeResponse>(
        `${API_URL}/api/v2/verification/email/verify`,
        { code },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate verification status to refetch
      void queryClient.invalidateQueries({
        queryKey: ["verification", "status"],
      });
    },
  });
};

/**
 * Send SMS verification code
 */
export const useSendSmsCode = () => {
  const { token } = useSession();

  return useMutation<SendCodeResponse, SendCodeError, void>({
    mutationFn: async () => {
      const response = await axios.post<SendCodeResponse>(
        `${API_URL}/api/v2/verification/sms/send`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
  });
};

/**
 * Verify SMS code
 */
export const useVerifySmsCode = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<VerifyCodeResponse, VerifyCodeError, { code: string }>({
    mutationFn: async ({ code }) => {
      const response = await axios.post<VerifyCodeResponse>(
        `${API_URL}/api/v2/verification/sms/verify`,
        { code },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate verification status to refetch
      void queryClient.invalidateQueries({
        queryKey: ["verification", "status"],
      });
    },
  });
};
